import { useState } from 'react';
import { useToast } from '../../context_providers/toast_context/ToastContext';
import { useOrganizationUpdaterProvider } from '../../context_providers/organization_context/OrganizationProvider';
import { useTranslation } from 'react-i18next';
import {
  ErrorStringKeys,
  SuccessStringKeys,
  StringKeys,
} from '../../resources/strings/StringKeys';
import type {
  PvdConfigurationOnAzure,
  AddPvdConfigRequest,
  UpdatePvdConfigRequest,
} from '../../../generated-client/models';
import {
  addPvdConfiguration,
  updatePvdConfiguration,
  deletePvdConfiguration,
  getAllPvdConfigurations,
  getPvdConfigurationByOrgCodeAndId,
} from '../../repository/PvdConfigDAO';

type OperationContext =
  | { operation: 'create'; data: AddPvdConfigRequest }
  | { operation: 'update'; data: UpdatePvdConfigRequest }
  | { operation: 'delete'; data: PvdConfigurationOnAzure };

interface UsePvdConfigOperationsOptions {
  onSuccess?: (config: PvdConfigurationOnAzure) => void;
  onError?: (error: unknown, context: OperationContext) => void;
  updateLocalState?: (
    operation: 'add' | 'update' | 'delete',
    config: PvdConfigurationOnAzure,
  ) => void;
}

export const usePvdConfigOperations = (options: UsePvdConfigOperationsOptions = {}) => {
  const [loading, setLoading] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  const { loadPvdConfigs } = useOrganizationUpdaterProvider();
  const { showErrorToast, showSuccessToast } = useToast();
  const { t } = useTranslation();

  const createConfig = async (
    request: AddPvdConfigRequest,
  ): Promise<PvdConfigurationOnAzure | null> => {
    if (inProgress) return null;
    const { onSuccess, onError, updateLocalState } = options;
    try {
      setLoading(true);
      setInProgress(true);
      const saved = await addPvdConfiguration(request);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_ADDED_CONFIGURATION, { configuration: request.spec_name }),
      );
      updateLocalState?.('add', saved);
      onSuccess?.(saved);
      loadPvdConfigs();
      return saved;
    } catch (error) {
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_ADD_CONFIGURATION, { configuration: request.spec_name }),
      );
      onError?.(error, { operation: 'create', data: request });
      return null;
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  const updateConfig = async (
    request: UpdatePvdConfigRequest,
  ): Promise<PvdConfigurationOnAzure | null> => {
    if (inProgress) return null;
    const { onSuccess, onError, updateLocalState } = options;
    try {
      setLoading(true);
      setInProgress(true);
      const result = await updatePvdConfiguration(request);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_UPDATED_CONFIGURATION, { configuration: request.spec_name }),
      );
      updateLocalState?.('update', result);
      onSuccess?.(result);
      loadPvdConfigs();
      return result;
    } catch (error) {
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_UPDATE_CONFIGURATION, { configuration: request.spec_name }),
      );
      onError?.(error, { operation: 'update', data: request });
      return null;
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  const deleteConfig = async (config: PvdConfigurationOnAzure): Promise<boolean> => {
    if (inProgress) return false;
    const { onSuccess, onError, updateLocalState } = options;
    try {
      setLoading(true);
      setInProgress(true);
      await deletePvdConfiguration({ org_code: config.org_code, config_id: config.id });
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_REMOVED_CONFIGURATION, { configuration: config.spec_name }),
      );
      updateLocalState?.('delete', config);
      onSuccess?.(config);
      loadPvdConfigs();
      return true;
    } catch (error: unknown) {
      // Match the existing UnifiedConfigDAO error treatment: the
      // "Cannot delete with org_users assigned" path uses a distinct toast.
      const errorMessage =
        (error as { message?: string })?.message ?? String(error);
      if (errorMessage.includes('Cannot delete org_spec with org_users assigned')) {
        showErrorToast(
          t(StringKeys.USERS_CURRENTLY_ASSIGNED_TO_CONFIGURATION, { configuration: config.spec_name }),
        );
      } else {
        showErrorToast(
          t(ErrorStringKeys.FAILED_TO_REMOVE_CONFIGURATION, { configuration: config.spec_name }),
        );
      }
      onError?.(error, { operation: 'delete', data: config });
      return false;
    } finally {
      setLoading(false);
      setInProgress(false);
    }
  };

  const fetchConfigs = async (orgCode: string): Promise<PvdConfigurationOnAzure[] | null> => {
    try {
      setLoading(true);
      return await getAllPvdConfigurations(orgCode);
    } catch {
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigById = async (
    orgCode: string,
    configId: string,
  ): Promise<PvdConfigurationOnAzure | null> => {
    try {
      setLoading(true);
      return await getPvdConfigurationByOrgCodeAndId(orgCode, configId);
    } catch {
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createConfig, updateConfig, deleteConfig, fetchConfigs, fetchConfigById, loading, inProgress };
};
